import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface ThemeContextType {
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  agencyColor: string | null;
  setAgencyColor: (color: string | null) => void;
}

const defaultPrimaryColor = '#f24b6a';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize agency color from localStorage if available
  const [agencyColor, setAgencyColorState] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem('agencyThemeColor');
      return stored || null;
    } catch {
      return null;
    }
  });

  // The actual primary color to use (agency color takes precedence)
  const [primaryColor, setPrimaryColorState] = useState<string>(() => {
    // Use stored agency color if available, otherwise default
    try {
      const stored = localStorage.getItem('agencyThemeColor');
      return stored || defaultPrimaryColor;
    } catch {
      return defaultPrimaryColor;
    }
  });

  // Update primary color when agency color changes
  useEffect(() => {
    const colorToUse = agencyColor || defaultPrimaryColor;
    setPrimaryColorState(colorToUse);
    document.documentElement.style.setProperty('--primary-color', colorToUse);
  }, [agencyColor]);

  // Set agency color and persist to localStorage
  const setAgencyColor = (color: string | null) => {
    setAgencyColorState(color);
    try {
      if (color) {
        localStorage.setItem('agencyThemeColor', color);
      } else {
        localStorage.removeItem('agencyThemeColor');
      }
    } catch (error) {
      console.error('Failed to persist agency theme color:', error);
    }
  };

  // Set primary color (used by admins to update agency theme)
  const setPrimaryColor = (color: string) => {
    setPrimaryColorState(color);
    document.documentElement.style.setProperty('--primary-color', color);
  };

  return (
    <ThemeContext.Provider value={{ primaryColor, setPrimaryColor, agencyColor, setAgencyColor }}>
      {children}
    </ThemeContext.Provider>
  );
}; 